import React, { useState } from "react";
import {
  DEFAULTS,
  computeScenarios,
  dustToVacations,
  dustToLumina,
} from "./s_calculator_helpers";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Legend, Filler);

function SCalculator() {

  const Info = ({ text }) => (
    <span
      role="img"
      aria-label={text}
      title={text}
      style={{ marginLeft: 8, color: '#cfc6e8', cursor: 'help', fontSize: 12 }}
    >
      ⓘ
    </span>
  );
  const [weekly, setWeekly] = useState(50);
  const [fontanaPrice, setFontanaPrice] = useState(1.0);
  const [period, setPeriod] = useState('1w');
  const [priceStep, setPriceStep] = useState(0.00001);
  const [periodsPerYear] = useState(DEFAULTS.periodsPerYear);
  const [mTrade, setMTrade] = useState(DEFAULTS.m_trade);
  const [marketCap, setMarketCap] = useState(1000000);
  const [assumptions, setAssumptions] = useState({
    r_hold: DEFAULTS.r_hold,
    dustPerVacation: DEFAULTS.dustPerVacation,
    luminaPerDustPerYear: DEFAULTS.luminaPerDustPerYear,
    dustPerDollar: DEFAULTS.dustPerDollar,
    dustConversionFactor: DEFAULTS.dustConversionFactor,
    buysPerWeek: 3,
    sellsPerWeek: 2,
    mode: 'yield',
  });

  // derive trading intensity from weekly trading amount: weekly $0 -> 0.5x, $2000 -> 2x
  const tradeIntensity = Math.min(2, Math.max(0.5, 0.5 + (weekly / 2000) * 1.5));
  const effectiveMultiplier = mTrade * tradeIntensity;

  // Fontana price slider mapping (log scale) and micro-step helpers
  const minPrice = 1e-8;
  const maxPrice = 100;
  const sliderMax = 1000;
  const priceToPos = (p) => {
    const clamped = Math.max(minPrice, Math.min(maxPrice, p));
    return Math.round(Math.log(clamped / minPrice) / Math.log(maxPrice / minPrice) * sliderMax);
  };
  const posToPrice = (pos) => {
    const t = Math.max(0, Math.min(sliderMax, Number(pos))) / sliderMax;
    return Number((minPrice * Math.pow(maxPrice / minPrice, t)).toFixed(12));
  };
  // (No +/- helpers; slider uses log mapping and direct position → price conversion.)

  const parsePeriodToYears = (p) => {
    switch (p) {
      case '1w': return 1/52;
      case '1m': return 1/12;
      case '1y': return 1;
      default: // '5y'
        return Number(p.replace('y','')) || 5;
    }
  };

  const years = parsePeriodToYears(period);

  // Map projection period to a displayed multiplier
  const periodMultiplierMap = {
    '1w': 0,
    '1m': 2,
    '1y': 20, //1m * 10
    '5y': 100, // 1y * no of years
  };
  const periodMultiplier = periodMultiplierMap[period] ?? 1;

  const projectionLabelMap = {
    '1w': 'Projection (1 week)',
    '1m': 'Projection (1 month)',
    '1y': 'Projection (1 year)',
    '5y': 'Projection (5 years)',
  };
  const projectionLabel = projectionLabelMap[period] ?? 'Projection (0–20 years)';

  const scenarios = computeScenarios({
    weeklyAmount: weekly,
    years,
    periodsPerYear,
    r_hold: assumptions.r_hold,
    m_trade: effectiveMultiplier,
    mode: assumptions.mode,
    dustPerDollar: DEFAULTS.dustPerDollar,
    dustConversionFactor: assumptions.dustConversionFactor,
    fontanaPrice,
    buysPerWeek: assumptions.buysPerWeek,
    sellsPerWeek: assumptions.sellsPerWeek,
  });

  // For the selected projection period, compute Holding dust using the
  // requested formula: dustPerDollar * buyMultiplier * periodMultiplier
  const holdDust = (
    Number(DEFAULTS.dustPerDollar || 0) *
    Number(DEFAULTS.buyDustMultiplier || 1) *
    Number(periodMultiplier || 0)
  );
  const tradeDust = scenarios.trade;

  // Projected trade counts for the selected period (informational)
  const totalWeeksForPeriod = periodsPerYear * years;
  const projectedBuys = Math.round(assumptions.buysPerWeek * totalWeeksForPeriod);
  const projectedSells = Math.round(assumptions.sellsPerWeek * totalWeeksForPeriod);

  // Sovereign Vault simulator: compute tax and allocations from market cap
  const minCap = 10000;
  const maxCap = 1000000000;

  // Slider mapping: slow (fine) steps up to `breakCap` (5M), then faster (coarse)
  const sliderSteps = 1000;
  const breakCap = 5000000; // 5 million

  const posToCap = (pos) => {
    const p = Math.max(0, Math.min(sliderSteps, Number(pos))) / sliderSteps;
    if (p <= 0.5) {
      // linear mapping from minCap -> breakCap across first half
      const t = p / 0.5;
      const v = minCap + t * (breakCap - minCap);
      return Math.max(minCap, Math.round(v / 10000) * 10000);
    }
    // second half: log-scale from breakCap -> maxCap
    const t = (p - 0.5) / 0.5;
    const v = Math.exp(Math.log(breakCap) + t * (Math.log(maxCap) - Math.log(breakCap)));
    return Math.max(breakCap, Math.round(v / 10000) * 10000);
  };

  const capToPos = (cap) => {
    const c = Math.max(minCap, Math.min(maxCap, Number(cap)));
    if (c <= breakCap) {
      const t = (c - minCap) / (breakCap - minCap);
      return Math.round(t * 0.5 * sliderSteps);
    }
    const t = (Math.log(c) - Math.log(breakCap)) / (Math.log(maxCap) - Math.log(breakCap));
    return Math.round((0.5 + 0.5 * t) * sliderSteps);
  };

  const capPos = capToPos(marketCap);
  const tax = Number(marketCap || 0) * 0.01; // 1% of market cap
  const goldAlloc = tax * 0.10; // 10% of tax
  const ceruleanAlloc = tax * 0.05; // 5% of tax
  const rvpAlloc = tax * 0.20; // 20% of tax
  const bonusAlloc = tax * 0.30; // 30% of tax (bonus pool)
  const maxTax = maxCap * 0.01;

  // 30-day aggregated view: multiply allocations by 30
  const goldAlloc30 = goldAlloc * 30;
  const ceruleanAlloc30 = ceruleanAlloc * 30;
  const rvpAlloc30 = rvpAlloc * 30;
  const bonusAlloc30 = bonusAlloc * 30;

  // Scale bar heights relative to maximum possible 30-day tax so visuals grow with market cap
  const maxTax30 = maxTax * 30;
  const goldPct30 = maxTax30 > 0 ? Math.round((goldAlloc30 / maxTax30) * 100) : 0;
  const ceruleanPct30 = maxTax30 > 0 ? Math.round((ceruleanAlloc30 / maxTax30) * 100) : 0;
  const rvpPct30 = maxTax30 > 0 ? Math.round((rvpAlloc30 / maxTax30) * 100) : 0;
  const bonusPct30 = maxTax30 > 0 ? Math.round((bonusAlloc30 / maxTax30) * 100) : 0;

  // Bar-chart style: fixed container height in px, map absolute 30-day allocation -> px
  // Use a small minimum visible pixel height so tiny increases remain visible.
  const containerHeightPx = 120;
  const minVisiblePx = 4;
  const goldFillPx = goldAlloc30 > 0 ? Math.max(minVisiblePx, Math.round((goldAlloc30 / maxTax30) * containerHeightPx)) : 0;
  const ceruleanFillPx = ceruleanAlloc30 > 0 ? Math.max(minVisiblePx, Math.round((ceruleanAlloc30 / maxTax30) * containerHeightPx)) : 0;
  const rvpFillPx = rvpAlloc30 > 0 ? Math.max(minVisiblePx, Math.round((rvpAlloc30 / maxTax30) * containerHeightPx)) : 0;
  const bonusFillPx = bonusAlloc30 > 0 ? Math.max(minVisiblePx, Math.round((bonusAlloc30 / maxTax30) * containerHeightPx)) : 0;

  const goldValueLabel30 = `$${Number(goldAlloc30 || 0).toLocaleString()}`;
  const ceruleanValueLabel30 = `$${Number(ceruleanAlloc30 || 0).toLocaleString()}`;
  const rvpValueLabel30 = `$${Number(rvpAlloc30 || 0).toLocaleString()}`;
  const bonusValueLabel30 = `$${Number(bonusAlloc30 || 0).toLocaleString()}`;

  // Reward chest math (updated):
  // 100K pool = 50% of Bonus pool
  const chest100kPool = 0.5 * bonusAlloc30;
  const num100kFull = Math.floor(chest100kPool / 100000);
  const remainder100k = chest100kPool % 100000;
  const chest100kFillPct = Math.round((remainder100k / 100000) * 100);

  // 1M jackpot rule: every 10 × 100K pools -> 1 × 1M jackpot
  const numJackpotFull = Math.floor(num100kFull / 10);
  const jackpotRemainderCount = num100kFull % 10;
  const jackpotFillPct = Math.round((jackpotRemainderCount / 10) * 100);

  const holdVac = dustToVacations(holdDust, DEFAULTS.dustPerVacation);
  const tradeVac = dustToVacations(tradeDust, DEFAULTS.dustPerVacation);

  const holdLum = dustToLumina(holdDust, DEFAULTS.luminaPerDustPerYear);
  const tradeLum = dustToLumina(tradeDust, DEFAULTS.luminaPerDustPerYear);
  const sellLum = scenarios.luminaFromSells || { total: 0, monthly: 0, annual: 0 };

  // Precompute fixed-horizon scenarios for cards
  const sc5 = computeScenarios({ weeklyAmount: weekly, years: 5, periodsPerYear, r_hold: assumptions.r_hold, m_trade: effectiveMultiplier, mode: assumptions.mode, dustPerDollar: DEFAULTS.dustPerDollar, dustConversionFactor: assumptions.dustConversionFactor, fontanaPrice, buysPerWeek: assumptions.buysPerWeek, sellsPerWeek: assumptions.sellsPerWeek });
  const sc10 = computeScenarios({ weeklyAmount: weekly, years: 10, periodsPerYear, r_hold: assumptions.r_hold, m_trade: effectiveMultiplier, mode: assumptions.mode, dustPerDollar: DEFAULTS.dustPerDollar, dustConversionFactor: assumptions.dustConversionFactor, fontanaPrice, buysPerWeek: assumptions.buysPerWeek, sellsPerWeek: assumptions.sellsPerWeek });
  const sc20 = computeScenarios({ weeklyAmount: weekly, years: 20, periodsPerYear, r_hold: assumptions.r_hold, m_trade: effectiveMultiplier, mode: assumptions.mode, dustPerDollar: DEFAULTS.dustPerDollar, dustConversionFactor: assumptions.dustConversionFactor, fontanaPrice, buysPerWeek: assumptions.buysPerWeek, sellsPerWeek: assumptions.sellsPerWeek });

  const makeCardData = (sc) => {
    const hold = sc.hold;
    const trade = sc.trade;
    const diff = trade - hold;
    const pct = hold === 0 ? (trade === 0 ? 0 : Infinity) : (diff / hold) * 100;
    return {
      hold,
      trade,
      diff,
      pct,
      holdVac: dustToVacations(hold, DEFAULTS.dustPerVacation),
      tradeVac: dustToVacations(trade, DEFAULTS.dustPerVacation),
      holdLum: dustToLumina(hold, DEFAULTS.luminaPerDustPerYear),
      tradeLum: dustToLumina(trade, DEFAULTS.luminaPerDustPerYear),
      sellLum: sc.luminaFromSells || { total: 0, monthly: 0, annual: 0 },
    };
  };

  const card5 = makeCardData(sc5);
  const card10 = makeCardData(sc10);
  const card20 = makeCardData(sc20);

  // Build chart data sampled across the selected projection `years`.
  // Generate human-friendly labels based on the chosen `period`.
  let steps;
  let labels;
  if (period === '1w') {
    // show days (0..7)
    const days = Math.max(1, Math.round(years * 365));
    steps = days + 1;
    labels = Array.from({ length: steps }, (_, i) => `${i}d`);
  } else if (period === '1m') {
    // show weeks (0..4)
    const weeks = Math.max(1, Math.round(years * 52));
    steps = weeks + 1;
    labels = Array.from({ length: steps }, (_, i) => `${i}w`);
  } else if (period >= 2 || period === '5y' || years >= 2) {
    // multi-year: show years (integer ticks)
    const yrs = Math.max(1, Math.ceil(years));
    steps = yrs + 1;
    labels = Array.from({ length: steps }, (_, i) => `${i}y`);
  } else if (years >= 1) {
    // ~1 year: show months
    const months = Math.max(1, Math.ceil(years * 12));
    steps = months + 1;
    labels = Array.from({ length: steps }, (_, i) => `${i}mo`);
  } else {
    // fallback: monthly-ish sampling
    steps = Math.max(6, Math.ceil(years * 12));
    labels = Array.from({ length: steps }, (_, i) => {
      const t = (i / Math.max(1, steps - 1)) * years;
      return Number(t.toFixed(2)).toString();
    });
  }
  const holdSeries = [];
  const tradeSeries = [];
  const pointRadiusHold = [];
  const pointRadiusTrade = [];

  // Compute Holding base and end values for interpolation
  const holdBase = Number(DEFAULTS.dustPerDollar || 0) * Number(DEFAULTS.buyDustMultiplier || 1);
  const holdEnd = holdBase * Number(periodMultiplier || 0);

  for (let i = 0; i < steps; i++) {
    const y = (i / Math.max(1, steps - 1)) * years;
    const sc = computeScenarios({ weeklyAmount: weekly, years: y, periodsPerYear, r_hold: assumptions.r_hold, m_trade: effectiveMultiplier, mode: assumptions.mode, dustPerDollar: DEFAULTS.dustPerDollar, dustConversionFactor: assumptions.dustConversionFactor, fontanaPrice, buysPerWeek: assumptions.buysPerWeek, sellsPerWeek: assumptions.sellsPerWeek });

    // Holding interpolation rules:
    // - For 1 week projection, show Holding = 0 across (no holding buildup).
    // - Otherwise, start at holdBase and linearly interpolate to holdEnd.
    let holdValue;
    if (period === '1w') {
      holdValue = 0;
    } else {
      const frac = steps > 1 ? (i / (steps - 1)) : 1;
      holdValue = Math.round(holdBase + frac * (holdEnd - holdBase));
    }

    // Active Trading uses computed scenario values; ensure final sample matches selected-period tradeDust
    let tradeValue = (i === steps - 1) ? Math.round(tradeDust) : Math.round(sc.trade);

    holdSeries.push(holdValue);
    tradeSeries.push(tradeValue);

    // emphasize approximate 5/10/20 year marks when they fall on the sampled t
    const highlight = (Math.abs(y - 5) < 0.0001 || Math.abs(y - 10) < 0.0001 || Math.abs(y - 20) < 0.0001) ? 6 : 2;
    pointRadiusHold.push(highlight);
    pointRadiusTrade.push(highlight);
  }

  const data = {
    labels,
    datasets: [
      {
        label: 'Holding',
        data: holdSeries,
        fill: true,
        backgroundColor: 'rgba(124, 211, 255, 0.06)',
        borderColor: '#7bdff0',
        tension: 0.25,
        pointRadius: pointRadiusHold,
      },
      {
        label: 'Active Trading',
        data: tradeSeries,
        fill: true,
        backgroundColor: 'rgba(255, 0, 204, 0.06)',
        borderColor: '#ff00cc',
        tension: 0.25,
        pointRadius: pointRadiusTrade,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: '#e9e7f1' } },
      tooltip: { mode: 'index', intersect: false },
    },
    interaction: { mode: 'nearest', axis: 'x', intersect: false },
    scales: {
      x: { ticks: { color: '#cfc6e8' }, grid: { color: '#1b1a2b' }, maxTicksLimit: Math.min(10, labels.length) },
      y: { ticks: { color: '#cfc6e8' }, grid: { color: '#1b1a2b' }, beginAtZero: true },
    },
  };

  return (
    <div className="container" style={{ paddingTop: 50, paddingBottom: 40, color: '#e9e7f1' }}>
      <h2 className="mt-5" style={{ color: '#ffd6f0', textShadow: '0 1px 2px rgba(0,0,0,0.6)' }}>Sovereign Simulators</h2>

      <div className="card mb-3" style={{ backgroundColor: '#0f1225', border: '1px solid #ff00cc' }}>
        <div className="card-body" style={{ color: '#e9e7f1' }}>
          <h5 style={{ color: '#ffd6f0', textAlign: 'left', marginBottom: 12 }}>Trading Simulator</h5>
          <div className="row align-items-center">
            <div className="col-md-6">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Active Trading toggle removed: always show both Holding and Active Trading */}
                

                <div>
                  <label className="form-label small mb-1" style={{ color: '#cfc6e8' }}>
                    Weekly trading amount
                    <Info text={"Controls the weekly $ you trade — affects trading intensity and multiplier."} />
                  </label>
                  <input
                    type="range"
                    className="form-range"
                    min={0}
                    max={2000}
                    step={1}
                    value={weekly}
                    onChange={(e) => setWeekly(Number(e.target.value))}
                  />
                  <div className="d-flex justify-content-between small text-muted">
                    <div>$0</div>
                    <div style={{ color: '#ffd6f0' }}>${weekly.toLocaleString()}</div>
                    <div>$2,000</div>
                  </div>
                  
                  <div className="small text-muted">Assumes <strong style={{ color: '#ffd6f0' }}>{assumptions.buysPerWeek}</strong> buys and <strong style={{ color: '#ffd6f0' }}>{assumptions.sellsPerWeek}</strong> sells per week</div>
                </div>

                <div>
                  <label className="form-label small mb-1" style={{ color: '#cfc6e8' }}>
                    Fontana price
                    <Info text={"Adjust the per-token price (log scale) for projection and conversion calculations."} />
                  </label>
                  <div className="d-flex align-items-center" style={{ gap: 8 }}>
                    <input
                      type="range"
                      className="form-range flex-grow-1"
                      min={0}
                      max={sliderMax}
                      step={1}
                      value={priceToPos(fontanaPrice)}
                      onChange={(e) => setFontanaPrice(posToPrice(e.target.value))}
                    />
                  </div>
                  <div className="d-flex justify-content-between mb-2 small text-muted">
                    <small>${minPrice}</small>
                    <strong style={{ color: '#ffddff' }}>${Number(fontanaPrice).toFixed(8)}</strong>
                    <small>${maxPrice}</small>
                  </div>
                </div>

                <div>
                    <label className="form-label">Projection period</label>
                  <select
                    className="form-select"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    style={{ backgroundColor: '#151632', color: '#e9e7f1', borderColor: '#2b2546' }}
                  >
                      <option value={'1w'}>1 week</option>
                      <option value={'1m'}>1 month</option>
                      <option value={'1y'}>1 year</option>
                      <option value={'5y'}>5 years</option>
                  </select>
                  <div className="small text-muted mt-1">Period multiplier: <strong style={{ color: '#ffd6f0' }}>{periodMultiplier}x</strong></div>
                </div>

                
              </div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Immediate preview</label>
              <div className="row g-2 mb-2">
                <div className="col-12 mb-1 small text-muted">Projected trades for period: <strong style={{ color: '#ffd6f0' }}>{projectedBuys.toLocaleString()}</strong> buys • <strong style={{ color: '#ffd6f0' }}>{projectedSells.toLocaleString()}</strong> sells</div>
                <div className="col-6">
                  <div className="card h-100" style={{ backgroundColor: '#12210f', border: '1px solid #7fbf3f' }}>
                      <div className="card-body">
                        <div className="small" style={{ color: '#e9f7d9' }}>RVP </div>
                        <div className="h5" style={{ color: '#f7ffce' }}>{Math.round(scenarios.rvpGenerated).toLocaleString()} RVP</div>
                        </div>
                  </div>
                </div>

                <div className="col-6">
                  <div className="card h-100" style={{ backgroundColor: '#1a0f28', border: '1px solid #9a61ff' }}>
                    <div className="card-body">
                        <div className="small" style={{ color: '#e9e0fb' }}>LUMINA</div>
                          <div className="h5" style={{ color: '#e8d6ff' }}>{Math.round(sellLum.total).toLocaleString()} LUMINA </div>
                          <div className="small text-muted" style={{ color: '#dfe9d0' }}>Total Fontana Sold: {Math.round(scenarios.totalFontanaBought).toLocaleString()}</div>
                      
                    </div>
                  </div>
                </div>

                <div className="col-6">
                  <div className="card h-100" style={{ backgroundColor: '#0f1225', border: '1px solid #2b2546' }}>
                    <div className="card-body">
                      <div className="small" style={{ color: '#bdb7d6' }}>Holding</div>
                      <div className="h5" style={{ color: '#7bdff0' }}>
                        {Math.round(holdDust).toLocaleString()} Dust
                      </div>
                      
                    </div>
                  </div>
                </div>

                <div className="col-6">
                  <div className="card h-100" style={{ backgroundColor: '#0f1225', border: '1px solid #2b2546' }}>
                    <div className="card-body">
                      <div className="small" style={{ color: '#bdb7d6' }}>Active Trading (multiplier applied)</div>
                      <div className="h5" style={{ color: '#7bdff0' }}>{Math.round(tradeDust).toLocaleString()} Dust</div>
                      <div className="mt-2 small" style={{ color: '#cfc6e8' }}>Difference: <strong style={{ color: '#ffd6f0' }}>{Math.round(tradeDust - holdDust).toLocaleString()} Dust</strong> • </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <button
          className="me-2"
          onClick={() => window.scrollTo(0,0)}
          style={{ backgroundColor: '#ff00cc', border: '1px solid #ff00cc', color: '#fff', padding: '8px 14px', borderRadius: 6 }}
        >
          Reset View
        </button>
      </div>

      <div className="card mb-3" style={{ backgroundColor: '#0f1225', border: '1px solid #2b2546', minHeight: 260 }}>
        <div className="card-body">
          <h5 className="card-title" style={{ color: '#ffd6f0' }}>{projectionLabel}</h5>
          <div style={{ height: 220 }}>
            <Line data={data} options={options} />
          </div>
        </div>
      </div>

          <div className="card mb-3" style={{ backgroundColor: '#0f1225', border: '1px solid #2b2546' }}>
        <div className="card-body">
          <h5 style={{ color: '#ffd6f0' }}>Sovereign Vault and Reward Simulators</h5>
          <div className="row">
            <div className="col-md-6">
              <label className="form-label small text-muted">
                Daily Market cap
                <Info text={"Market cap drives the 1% tax. Higher market cap increases vault allocations."} />
              </label>
              {/* market cap slider: slow steps up to 5M, then quicker to 1B */}
              <input
                type="range"
                className="form-range"
                min={0}
                max={sliderSteps}
                step={1}
                value={capPos}
                onChange={(e) => setMarketCap(posToCap(e.target.value))}
              />
              <div className="d-flex justify-content-between small text-muted">
                <div>10K</div>
                <div style={{ color: '#ffd6f0' }}>${Number(marketCap).toLocaleString()}</div>
                <div>1B</div>
              </div>

              {/* Vault visuals moved here: Gold, Cerulean, RVP */}
              <div style={{ marginTop: 18 }}>
                
                    
              <label className="form-label small text-muted">Vault allocations (30 days)</label>
              <div className="card" style={{ backgroundColor: '#0f1225', border: '1px solid #2b2546' }}>
                <div className="card-body" style={{ padding: 12 }}>
                  <div style={{ height: 200 }}>
                    {/* Bar chart showing absolute 30-day allocations for each pool */}
                    <Bar
                      data={{
                        labels: ['Gold', 'Cerulean', 'RVP', 'Bonus'],
                        datasets: [{
                          label: '30-day allocation ($)',
                          data: [goldAlloc30, ceruleanAlloc30, rvpAlloc30, bonusAlloc30],
                          backgroundColor: ['#b8860b', '#0ea5a4', '#3bd18f', '#ff8c00'],
                          borderColor: ['#a67c00', '#089b8b', '#2fae6f', '#ff7a00'],
                          borderWidth: 1,
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => `$${Number(ctx.parsed.y).toLocaleString()}` } } },
                        scales: {
                          x: { grid: { display: false }, ticks: { color: '#cfc6e8' } },
                          y: { beginAtZero: true, ticks: { color: '#cfc6e8', callback: (val) => `$${Number(val).toLocaleString()}` }, grid: { color: '#1b1a2b' } }
                        }
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, gap: 12 }}>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ color: '#e8ffd6', fontWeight: 600 }}>Gold Vault</div>
                      <div className="small text-muted">{goldValueLabel30}</div>
                    </div>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ color: '#e8ffd6', fontWeight: 600 }}>Cerulean Vault</div>
                      <div className="small text-muted">{ceruleanValueLabel30}</div>
                    </div>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ color: '#e8ffd6', fontWeight: 600 }}>RVP Pool</div>
                      <div className="small text-muted">{rvpValueLabel30}</div>
                    </div>
                    <div style={{ textAlign: 'center', flex: 1 }}>
                      <div style={{ color: '#e8ffd6', fontWeight: 600 }}>Bonus Pool</div>
                      <div className="small text-muted">{bonusValueLabel30}</div>
                    </div>
                  </div>
                </div>
              </div>
            

              </div>

            </div>

            <div className="col-md-6">
              <label className="form-label small text-muted">Reward Chests</label>
              <div className="card h-100" style={{ backgroundColor: '#0f1225', border: '1px solid #2b2546' }}>
                <div className="card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div>
                      <div style={{ color: '#e8ffd6', fontWeight: 700, marginBottom: 6 }}>100K Chest</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ height: 18, background: '#08110d', border: '1px solid #163b2d', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ width: `${chest100kFillPct}%`, height: '100%', background: 'linear-gradient(90deg,#ffd700,#b8860b)', transition: 'width 300ms ease' }} />
                          </div>
                          <div className="small text-muted mt-2">{num100kFull} full • {chest100kFillPct}% to next 100K</div>
                        </div>
                        <div style={{ width: 80, textAlign: 'center' }}>
                          <div style={{ color: '#e8ffd6', fontWeight: 700 }}>{num100kFull}</div>
                          <div className="small text-muted">x100K</div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <div style={{ color: '#e8ffd6', fontWeight: 700, marginBottom: 6 }}>1M Jackpot</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ height: 18, background: '#08110d', border: '1px solid #3b2a00', borderRadius: 6, overflow: 'hidden' }}>
                            <div style={{ width: `${jackpotFillPct}%`, height: '100%', background: 'linear-gradient(90deg,#ffbf00,#ff8c00)', transition: 'width 300ms ease' }} />
                          </div>
                          <div className="small text-muted mt-2">{numJackpotFull} full • {jackpotFillPct}% to next Jackpot</div>
                        </div>
                        <div style={{ width: 80, textAlign: 'center' }}>
                          <div style={{ color: '#e8ffd6', fontWeight: 700 }}>{numJackpotFull}</div>
                          <div className="small text-muted">x1M</div>
                        </div>
                      </div>
                    </div>

                    <div className="small text-muted">Formula: 100K pool = 50% × Bonus pool</div>
                    <div className="small text-muted">Formula: 1M Jackpot = floor(100K_count / 10)</div>
                  </div>
                </div>
              </div>
            </div>
           
          </div>
          {/* Vault visuals moved into left column below the slider */}
          
        </div>
      </div>
    </div>
      
      
    
  );
}

export default SCalculator;
